import { createRoot } from 'react-dom/client';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from 'styled-components';
import { client } from '@xbcb/apollo-client';
import { Provider } from 'react-redux';
import create from './store';
import ScrollToTop from './scroll';
// APP has to be imported AFTER reduxStore to avoid circular dependency issues (only when building production site)
import App from 'components/App';
import { UiStage, uiStageToBackendStage } from '@xbcb/ui-types';
import { isAglPortal } from '@xbcb/ui-env';
import { getEnv } from '@xbcb/ui-utils';
import { primaryTheme } from '@xbcb/ui-styles';
import { awsRumClient } from '@xbcb/cloudwatch-rum';
import DevTools from 'devtools/DevTools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { MbmProvider } from '@amzn/react-arb-tools';
import arbManifest from 'translations/arbManifest.js';
import { translationCdnUrls } from 'types';
import { isUiLocalizationEnabled } from 'featureFlags';
import '@amzn/katal-components/styles.css';

const VALID_US_LOCALE = 'en-US';

const { stage, version, REACT_APP_STAGE } = getEnv();
export const store = create();

// Let the compiler know we expect this on the interface
declare global {
  interface Window {
    Cypress?: unknown;
  }
}
if (!window.Cypress) {
  awsRumClient.initClient(stage, version);
}

declare const __ENV__: Record<string, string> | undefined;

export const localizationContextBuilder = new LocalizationContextBuilder();

/**
 * starts react
 */
const main = async () => {
  const store = create();
  const isAgl = isAglPortal();
  if (REACT_APP_STAGE) {
    if (REACT_APP_STAGE !== UiStage.PROD) {
      window.dispatch = store.dispatch;
    }

    const locale = new Intl.Locale(navigator.languages?.[0]);
    const translationCdnUrl = translationCdnUrls[uiStageToBackendStage[stage]];

    const mbmOption = {
      arbManifest,
      defaultLocalizationContext: localizationContextBuilder
        .withLocale(isUiLocalizationEnabled ? locale.baseName : VALID_US_LOCALE)
        .withDefaultLocale(VALID_US_LOCALE)
        .build(),
      resolveBundleUrl: (url?: string | undefined) =>
        url ? `${translationCdnUrl}/translations/${url}` : undefined,
    };

    const root = createRoot(document.getElementById('root') as HTMLElement);

    root.render(
      <Provider store={store}>
        <MbmProvider {...mbmOption}>
          <ApolloProvider client={client}>
            <BrowserRouter basename={isAgl ? '/cbms' : undefined}>
              <ThemeProvider theme={primaryTheme}>
                <ScrollToTop>
                  <App />
                </ScrollToTop>
              </ThemeProvider>
            </BrowserRouter>
          </ApolloProvider>
        </MbmProvider>
        {REACT_APP_STAGE !== UiStage.PROD ? <DevTools /> : null}
      </Provider>,
    );
  } else {
    throw new Error('REACT_APP_STAGE not set in environment');
  }
};

void main();
